import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import useManagementStore from '../../../../store/managementStore';
import {
  sendEmail,
  unassignEmailInteraction,
  getEmailSender,
} from '../../../../services/emailService';
import EmailMessage from './components/EmailMessage';
import EmailComposer from './components/EmailComposer';
import EmailEmptyState from './components/EmailEmptyState';
import { EnvelopeIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const EmailChat = ({ interactions, expedientId, debtorId, debtor }) => {
  const { addInteraction, removeInteraction } = useManagementStore();
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);

  const [isSending, setIsSending] = useState(false);
  const [replyToThreadId, setReplyToThreadId] = useState(null);
  const [replyToSubject, setReplyToSubject] = useState('');
  const [sender, setSender] = useState(null);

  // Con qué identidad sale el correo. Si falla la consulta se deja en null y la
  // pantalla no dice nada: es información, no una puerta. La puerta de verdad
  // está en el backend, que es fail-closed.
  useEffect(() => {
    if (!expedientId) return;
    let cancelled = false;

    getEmailSender(expedientId)
      .then(data => {
        if (!cancelled) setSender(data);
      })
      .catch(() => {
        if (!cancelled) setSender(null);
      });

    return () => {
      cancelled = true;
    };
  }, [expedientId]);

  // Auto-scroll to bottom when messages change
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [interactions]);

  const handleReply = (threadId, subject) => {
    setReplyToThreadId(threadId);
    setReplyToSubject(subject || '');
  };

  const handleCancelReply = () => {
    setReplyToThreadId(null);
    setReplyToSubject('');
  };

  const handleUnassign = async interaction => {
    const interactionId = interaction.interaction_id || interaction._id;
    if (!window.confirm('Este email se moverá a "Emails sin asignar". ¿Continuar?')) return;

    try {
      await unassignEmailInteraction(interactionId);
      removeInteraction(expedientId, interactionId);
      toast.success('Email desasignado correctamente');
    } catch (error) {
      console.error('Error unassigning email:', error);
      toast.error(error.response?.data?.message || 'Error al desasignar el email');
    }
  };

  const handleSend = async emailData => {
    if (!expedientId) {
      toast.error('Faltan identificadores para enviar el email');
      return;
    }

    setIsSending(true);

    try {
      const response = await sendEmail(expedientId, emailData);

      // Add the interaction to the store
      addInteraction(expedientId, response);

      toast.success('Email enviado correctamente');

      // Reset reply mode
      handleCancelReply();
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.response?.data?.message || 'Error al enviar el email');
    } finally {
      setIsSending(false);
    }
  };

  // Sort interactions by date (oldest first for chat view)
  const sortedInteractions = [...interactions].sort(
    (a, b) => new Date(a.date) - new Date(b.date)
  );

  return (
    <div className='h-[calc(100vh-315px)] bg-gray-50 flex flex-col'>
      {/* Chat Header */}
      <div className='bg-white border-b border-slate-200 px-3 py-2 shrink-0'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <span className='font-semibold text-slate-900 text-sm'>Email</span>
            <span className='text-xs text-slate-500'>
              {debtor?.debtormail || 'Conversación por correo'}
            </span>
          </div>
          <EnvelopeIcon className='w-5 h-5 text-gray-400' />
        </div>

        {/* Desde qué dirección sale. En AR es la de la empresa cliente, no la
            de ClaimUp, y el gestor tiene que verlo antes de escribir. */}
        {sender?.from && (
          <div className='text-xs text-slate-500 mt-1 truncate' title={sender.from}>
            Se enviará desde <span className='text-slate-700'>{sender.from}</span>
          </div>
        )}
      </div>

      {/* El envío está bloqueado: se dice aquí y no al pulsar Enviar, para no
          hacer redactar un correo que no va a salir. */}
      {sender && !sender.can_send && (
        <div className='bg-amber-50 border-b border-amber-200 px-3 py-2 flex items-start gap-2 shrink-0'>
          <ExclamationTriangleIcon className='w-4 h-4 text-amber-600 shrink-0 mt-0.5' />
          <div className='text-xs text-amber-800'>
            <span className='font-medium'>No se puede enviar correo ahora mismo.</span>{' '}
            {sender.message}
          </div>
        </div>
      )}

      {/* Email Messages */}
      <div
        ref={messagesContainerRef}
        className={`flex-1 p-4 min-h-0 flex flex-col ${
          interactions.length === 0
            ? 'items-center justify-center'
            : 'overflow-y-auto'
        }`}
      >
        {interactions.length === 0 ? (
          <EmailEmptyState />
        ) : (
          <div className='space-y-3 w-full'>
            {(() => {
              let lastDate = null;

              return sortedInteractions.map((interaction, index) => {
                const currentDate = moment(interaction.date).format('DD/MM/YYYY');
                const showDate = currentDate !== lastDate;
                lastDate = currentDate;

                return (
                  <div key={`${interaction.interaction_id || interaction._id}-${index}`}>
                    {/* Date Separator */}
                    {showDate && (
                      <div className='flex justify-center my-4'>
                        <div className='bg-gray-200 text-gray-600 text-xs px-3 py-1 rounded-full'>
                          {currentDate}
                        </div>
                      </div>
                    )}

                    {/* Email Message */}
                    <EmailMessage
                      interaction={interaction}
                      onReply={handleReply}
                      onUnassign={handleUnassign}
                      defaultExpanded={index === sortedInteractions.length - 1}
                    />
                  </div>
                );
              });
            })()}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Email Composer */}
      <EmailComposer
        expedientId={expedientId}
        onSend={handleSend}
        isSending={isSending}
        replyToThreadId={replyToThreadId}
        replyToSubject={replyToSubject}
        onCancelReply={handleCancelReply}
        disabled={sender ? !sender.can_send : false}
      />
    </div>
  );
};

export default EmailChat;
